if (!customElements.get('mojo-review-story-v2')) {
  customElements.define('mojo-review-story-v2', class extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.events = new AbortController();
      this.requests = new Set();
      this.cache = new Map();
      this.inflight = new Map();
      this.products = new Map();
      this.seedPages = [];
      this.index = 0;
      this.listToken = 0;
      this.listIds = new Set();
      this.engaged = false;
      this.l = this.parse('[data-copy]', {});
      this.cardSlot = this.querySelector('[data-card]');
      this.allDialog = this.querySelector('[data-all]');
      this.mediaDialog = this.querySelector('[data-media]');
      this.filter = this.querySelector('[data-filter]');
      this.sort = this.querySelector('[data-sort]');
      this.querySelectorAll('[data-seed]').forEach(node => {
        try {
          let data = JSON.parse(node.textContent);
          if (typeof data === 'string') data = JSON.parse(data);
          if (Array.isArray(data.reviews)) this.seedPages.push(data);
        } catch (_) {}
      });
      const subscriber = this.dataset.store || this.seedPages.flatMap(p => p.reviews).find(r => r.subscriberId)?.subscriberId;
      this.base = /^[a-f0-9-]{36}$/i.test(subscriber || '') ? 'https://api.okendo.io/v1/stores/' + subscriber : '';
      this.available = this.unique(this.seedPages.flatMap(p => this.clean(p.reviews)));
      this.parse('[data-products]', []).forEach(p => {
        if (p && p.id && p.name) this.products.set(p.id, p);
      });
      this.addProducts(this.available);
      this.featured = this.select(this.available);
      const signal = this.events.signal;
      this.addEventListener('click', event => this.click(event), { signal });
      this.addEventListener('pointerdown', () => { this.engaged = true; }, { passive: true, signal });
      this.addEventListener('focusin', () => { this.engaged = true; }, { signal });
      this.filter.addEventListener('change', () => this.loadList(true), { signal });
      this.sort.addEventListener('change', () => this.loadList(true), { signal });
      [this.allDialog, this.mediaDialog].forEach(dialog => {
        dialog.addEventListener('close', () => this.afterClose(dialog), { signal });
        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close(dialog);
        }, { signal });
      });
      this.mediaDialog.addEventListener('keydown', event => {
        if (event.target.closest('video')) return;
        if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault(); this.mediaStep(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }, { signal });
      let touch;
      this.cardSlot.addEventListener('touchstart', event => {
        touch = event.touches.length === 1 ? [event.touches[0].clientX, event.touches[0].clientY] : null;
      }, { passive: true, signal });
      this.cardSlot.addEventListener('touchend', event => {
        if (touch && event.changedTouches.length && !event.target.closest('a,button,video')) {
          const dx = event.changedTouches[0].clientX - touch[0], dy = event.changedTouches[0].clientY - touch[1];
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.step(dx > 0 ? -1 : 1);
        }
        touch = null;
      }, { passive: true, signal });
      this.classList.add('is-ready');
      this.querySelector('[data-browse]').hidden = false;
      this.render();
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.refresh(); }
        }, { rootMargin: '160px' });
        this.observer.observe(this);
      } else this.refresh();
    }
    disconnectedCallback() {
      this.events?.abort(); this.observer?.disconnect();
      this.requests?.forEach(controller => controller.abort());
      this.stopMedia();
      for (const dialog of [this.mediaDialog, this.allDialog]) if (dialog?.open) dialog.close();
      this.unlock(); this.started = false;
    }
    parse(selector, fallback) { try { return JSON.parse(this.querySelector(selector)?.textContent || ''); } catch (_) { return fallback; } }
    el(tag, name, text) {
      const node = document.createElement(tag);
      if (name) node.className = name;
      if (text !== undefined) node.textContent = String(text);
      return node;
    }
    button(text, name = 'mrc2__link') { const node = this.el('button', name, text); node.type = 'button'; return node; }
    mediaURL(value) {
      if (!value) return '';
      try { const u = new URL(value, 'https://mojokoe.com'); return u.protocol === 'https:' ? u.href : ''; } catch (_) { return ''; }
    }
    productURL(value) {
      if (!value) return '';
      try {
        const u = new URL(value, location.origin === 'null' ? 'https://mojokoe.com' : location.origin);
        const hosts = [location.hostname, 'mojokoe.com', 'www.mojokoe.com', this.dataset.shopHost];
        if (!hosts.includes(u.hostname) || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(u.pathname)) return '';
        const preview = new URLSearchParams(location.search).get('preview_theme_id') || this.dataset.preview;
        if (preview && /^\d+$/.test(preview)) u.searchParams.set('preview_theme_id', preview);
        return u.pathname + u.search + u.hash;
      } catch (_) { return ''; }
    }
    clean(items) {
      return Array.isArray(items) ? items.filter(r => r && typeof r.reviewId === 'string' && (!r.status || r.status === 'approved') && Number.isInteger(Number(r.rating)) && Number(r.rating) >= 1 && Number(r.rating) <= 5) : [];
    }
    unique(items) { return [...new Map(items.map(r => [r.reviewId, r])).values()]; }
    select(items) {
      const ordered = this.unique(items).filter(r => String(r.body || '').trim()).sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
      const candidates = ordered.filter(r => String(r.body).trim().length >= 28);
      const result = [], products = new Set();
      for (const r of (candidates.length ? candidates : ordered)) {
        if (!products.has(r.productId)) { result.push(r); products.add(r.productId); }
        if (result.length === 8) break;
      }
      for (const r of ordered) { if (result.length === 8) break; if (!result.some(x => x.reviewId === r.reviewId)) result.push(r); }
      return result;
    }
    addProducts(reviews) {
      for (const r of reviews) if (r.productId && r.productName && !this.products.has(r.productId)) this.products.set(r.productId, { id: r.productId, name: r.productName, url: r.productUrl });
      const current = this.filter.value;
      this.filter.replaceChildren(new Option(this.l.all_products, ''));
      [...this.products.values()].sort((a, b) => a.name.localeCompare(b.name)).forEach(p => this.filter.add(new Option(p.name, p.id)));
      this.filter.value = this.products.has(current) ? current : '';
    }
    endpoint(product = '', order = 'date desc') {
      if (!this.base) return '';
      if (!['date desc', 'date asc', 'rating desc', 'rating asc'].includes(order)) order = 'date desc';
      return this.base + (product ? '/products/' + encodeURIComponent(product) : '') + '/reviews?limit=25&orderBy=' + encodeURIComponent(order);
    }
    apiURL(value, previous = '') {
      if (!value || !this.base) return '';
      try {
        let u;
        if (/^https:\/\//.test(value)) u = new URL(value);
        else if (value.startsWith('/v1/stores/')) u = new URL(value, 'https://api.okendo.io');
        else if (value.startsWith('?')) u = new URL(value, previous || this.endpoint());
        else u = new URL(this.base + '/' + value.replace(/^\//, ''));
        return u.origin === 'https://api.okendo.io' && u.href.startsWith(this.base + '/') ? u.href : '';
      } catch (_) { return ''; }
    }
    async get(value) {
      const url = this.apiURL(value);
      if (!url) throw new Error('review_source_unavailable');
      if (this.cache.has(url)) return this.cache.get(url);
      if (this.inflight.has(url)) return this.inflight.get(url);
      const operation = (async () => {
        const controller = new AbortController(); this.requests.add(controller);
        const timer = setTimeout(() => controller.abort(), 10000);
        try {
          const response = await fetch(url, { credentials: 'omit', signal: controller.signal });
          if (!response.ok) throw new Error('review_http_' + response.status);
          const data = await response.json();
          if (!Array.isArray(data.reviews)) throw new Error('review_response_invalid');
          const result = { reviews: this.clean(data.reviews), next: this.apiURL(data.nextUrl || data.reviewsNextUrl || '', url) };
          this.cache.set(url, result); return result;
        } finally { clearTimeout(timer); this.requests.delete(controller); }
      })();
      this.inflight.set(url, operation);
      try { return await operation; } finally { this.inflight.delete(url); }
    }
    async refresh() {
      if (this.refreshing) return;
      this.refreshing = true;
      try {
        const data = await this.get(this.endpoint());
        if (!this.isConnected) return;
        this.available = this.unique([...this.available, ...data.reviews]); this.addProducts(data.reviews);
        this.dataset.sourceState = 'live';
        if (!this.engaged || !this.featured.length) { this.featured = this.select(this.available); this.index = 0; this.render(); }
      } catch (_) {
        if (!this.isConnected) return;
        this.dataset.sourceState = 'saved';
        if (!this.featured.length) this.render();
      } finally { this.refreshing = false; }
    }
    render() {
      this.index = Math.max(0, Math.min(this.index, this.featured.length - 1));
      this.cardSlot.replaceChildren();
      this.cardSlot.setAttribute('aria-busy', 'false');
      if (this.featured.length) this.cardSlot.append(this.card(this.featured[this.index], true));
      else {
        this.cardSlot.append(this.el('p', 'mrc2__notice', this.l.unavailable));
        const retry = this.button(this.l.retry, 'mrc2__outline'); retry.dataset.retryFeatured = ''; this.cardSlot.append(retry);
      }
      this.querySelector('[data-navigation]').hidden = this.featured.length < 2;
      const counter = this.querySelector('[data-counter]');
      counter.textContent = (this.index + 1).toString().padStart(2, '0') + ' / ' + this.featured.length.toString().padStart(2, '0');
      counter.setAttribute('aria-label', this.l.review + ' ' + (this.index + 1) + ' ' + this.l.out_of + ' ' + this.featured.length);
    }
    step(direction) { if (this.featured.length > 1) { this.index = (this.index + direction + this.featured.length) % this.featured.length; this.render(); } }
    mediaFor(r) {
      return (Array.isArray(r?.media) ? r.media : []).filter(m => ['image', 'video'].includes(m.type) && this.mediaURL(m.fullSizeUrl || m.largeUrl || m.videoUrls?.fullSizeUrl)).map(m => ({ ...m, fullSizeUrl: m.fullSizeUrl || m.largeUrl || m.videoUrls?.fullSizeUrl, thumbnailUrl: m.thumbnailUrl || m.videoUrls?.thumbnailUrl, videoPosterUrl: m.videoPosterUrl || m.videoUrls?.videoPosterUrl }));
    }
    card(r, feature = false) {
      const article = this.el('article', 'mrc2__card' + (feature ? ' mrc2__card--featured' : ''));
      const rating = this.el('div', 'mrc2__stars', '★'.repeat(Number(r.rating)) + '☆'.repeat(5 - Number(r.rating)));
      rating.setAttribute('role', 'img'); rating.setAttribute('aria-label', this.l.rating + ' ' + r.rating + ' ' + this.l.out_of + ' 5'); article.append(rating);
      const body = String(r.body || r.title || ''), quote = this.el(feature ? 'blockquote' : 'p', 'mrc2__quote', body);
      article.append(quote);
      if (body.length > 145 || body.split('\n').length > 2) {
        quote.classList.add('is-clamped');
        const more = this.button(this.l.read_more); more.setAttribute('aria-expanded', 'false');
        more.addEventListener('click', () => { const open = more.getAttribute('aria-expanded') !== 'true'; quote.classList.toggle('is-clamped', !open); more.setAttribute('aria-expanded', String(open)); more.textContent = open ? this.l.read_less : this.l.read_more; });
        article.append(more);
      }
      const byline = this.el('div', 'mrc2__byline'); byline.append(this.el('strong', '', r.reviewer?.displayName || this.l.customer));
      const date = new Date(r.dateCreated);
      if (!Number.isNaN(date.getTime())) { const time = this.el('time', '', new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date)); time.dateTime = r.dateCreated; byline.append(time); }
      article.append(byline);
      if (r.reviewer?.isVerified === true) article.append(this.el('p', 'mrc2__disclosure', '✓ ' + this.l.verified));
      if (r.isIncentivized === true) article.append(this.el('p', 'mrc2__disclosure', this.l.incentive));
      const p = this.products.get(r.productId), name = p?.name || r.productName, href = this.productURL(p?.url || r.productUrl);
      if (name) { const product = this.el(href ? 'a' : 'span', 'mrc2__product', name); if (href) { product.href = href; product.setAttribute('aria-label', this.l.reviewed_product + ': ' + name); } article.append(product); }
      const media = this.mediaFor(r);
      if (media.length) {
        const group = this.el('div', 'mrc2__media-row'); group.setAttribute('role', 'group'); group.setAttribute('aria-label', this.l.media + ' · ' + (r.reviewer?.displayName || this.l.customer));
        media.slice(0, 3).forEach((m, index) => {
          const button = this.button('', 'mrc2__thumb'); button.setAttribute('aria-label', (m.type === 'video' ? this.l.video : this.l.photo) + ' ' + (index + 1));
          const url = this.mediaURL(m.thumbnailUrl || m.videoPosterUrl || (m.type === 'image' ? m.fullSizeUrl : ''));
          if (url) { const img = this.el('img'); img.src = url; img.alt = ''; img.loading = 'lazy'; img.width = 64; img.height = 64; img.addEventListener('error', () => { img.remove(); button.classList.add('is-unavailable'); }, { once: true }); button.append(img); }
          if (m.type === 'video') button.append(this.el('span', 'mrc2__play', '▶'));
          button.append(this.el('span', 'mrc2__thumb-label', m.type === 'video' ? this.l.video_short : this.l.photo_short));
          button.addEventListener('click', () => this.openMedia(r, index, button)); group.append(button);
        });
        if (media.length > 3) { const more = this.button('+' + (media.length - 3), 'mrc2__thumb mrc2__thumb--more'); more.setAttribute('aria-label', this.l.more_media); more.addEventListener('click', () => this.openMedia(r, 3, more)); group.append(more); }
        article.append(group);
      }
      return article;
    }
    click(event) {
      const b = event.target.closest('button'); if (!b || !this.contains(b)) return;
      if (b.hasAttribute('data-prev')) this.step(-1);
      else if (b.hasAttribute('data-next')) this.step(1);
      else if (b.hasAttribute('data-browse')) { this.open(this.allDialog, b); this.loadList(true); }
      else if (b.hasAttribute('data-close')) this.close(b.closest('dialog'));
      else if (b.hasAttribute('data-load-more')) this.loadList(false);
      else if (b.hasAttribute('data-retry')) { this.cache.delete(this.endpoint(this.filter.value, this.sort.value)); this.loadList(true); }
      else if (b.hasAttribute('data-retry-featured')) this.refresh();
      else if (b.hasAttribute('data-media-prev')) this.mediaStep(-1);
      else if (b.hasAttribute('data-media-next')) this.mediaStep(1);
    }
    open(dialog, opener) {
      dialog._opener = opener;
      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else { dialog.setAttribute('open', ''); dialog.classList.add('mrc2__dialog--fallback'); dialog.setAttribute('role', 'dialog'); dialog.setAttribute('aria-modal', 'true'); }
      }
      if (!this.locked) { this.oldOverflow = document.documentElement.style.overflow; document.documentElement.style.overflow = 'hidden'; this.locked = true; }
      dialog.querySelector('[data-close]').focus({ preventScroll: true });
    }
    close(dialog) { if (!dialog) return; if (typeof dialog.close === 'function') dialog.close(); else { dialog.removeAttribute('open'); this.afterClose(dialog); } }
    afterClose(dialog) {
      if (dialog === this.mediaDialog) this.stopMedia();
      if (!this.allDialog.open && !this.mediaDialog.open) this.unlock();
      if (dialog._opener?.isConnected) dialog._opener.focus({ preventScroll: true });
    }
    unlock() { if (this.locked) { document.documentElement.style.overflow = this.oldOverflow; this.locked = false; } }
    savedForFilter() {
      const id = this.filter.value, order = this.sort.value;
      return this.available.filter(r => !id || r.productId === id).sort((a, b) => order === 'rating asc' ? a.rating - b.rating || new Date(b.dateCreated) - new Date(a.dateCreated) : order === 'rating desc' ? b.rating - a.rating || new Date(b.dateCreated) - new Date(a.dateCreated) : new Date(b.dateCreated) - new Date(a.dateCreated));
    }
    fillList(reviews, replace) {
      const list = this.querySelector('[data-list]');
      if (replace) { list.replaceChildren(); this.listIds = new Set(); }
      for (const r of reviews) if (!this.listIds.has(r.reviewId)) { this.listIds.add(r.reviewId); list.append(this.card(r)); }
    }
    async loadList(reset) {
      if (!reset && this.listBusy) return;
      const token = ++this.listToken, list = this.querySelector('[data-list]'), status = this.querySelector('[data-list-status]'), more = this.querySelector('[data-load-more]'), retry = this.querySelector('[data-retry]');
      const url = reset ? this.endpoint(this.filter.value, this.sort.value) : this.listNext;
      if (!reset && !url) return;
      this.listBusy = true;
      if (reset) { this.listNext = ''; this.fillList(this.savedForFilter(), true); }
      status.textContent = this.l.loading; more.disabled = true; more.hidden = true; retry.hidden = true; list.setAttribute('aria-busy', 'true');
      try {
        const data = await this.get(url);
        if (token !== this.listToken || !this.isConnected) return;
        this.available = this.unique([...this.available, ...data.reviews]); this.addProducts(data.reviews);
        this.fillList(data.reviews, reset); this.listNext = data.next;
        status.textContent = this.listIds.size ? this.listIds.size + ' ' + this.l.count : this.l.empty;
        more.hidden = !data.next; this.dataset.listState = 'live';
      } catch (_) {
        if (token !== this.listToken || !this.isConnected) return;
        this.dataset.listState = 'saved';
        status.textContent = this.listIds.size ? this.l.saved_notice : this.l.unavailable;
        retry.hidden = false; more.hidden = true; this.listNext = url;
        if (!reset && url) { more.hidden = false; more.textContent = this.l.retry_more; }
      } finally {
        if (token === this.listToken) { this.listBusy = false; more.disabled = false; list.setAttribute('aria-busy', 'false'); if (this.dataset.listState === 'live') more.textContent = this.l.more; }
      }
    }
    openMedia(review, index, opener) { this.mediaReview = review; this.mediaIndex = index; this.open(this.mediaDialog, opener); this.renderMedia(); }
    stopMedia() { this.querySelectorAll('[data-stage] video').forEach(v => { v.pause(); v.removeAttribute('src'); v.load(); }); }
    mediaStep(direction) { const items = this.mediaFor(this.mediaReview); if (items.length > 1) { this.mediaIndex = (this.mediaIndex + direction + items.length) % items.length; this.renderMedia(); } }
    renderMedia() {
      this.stopMedia();
      const stage = this.querySelector('[data-stage]'); stage.replaceChildren();
      const r = this.mediaReview, items = this.mediaFor(r), m = items[this.mediaIndex]; if (!m) return;
      const src = this.mediaURL(m.fullSizeUrl);
      let node;
      if (m.type === 'video') { node = this.el('video'); node.controls = true; node.playsInline = true; node.preload = 'none'; const poster = this.mediaURL(m.videoPosterUrl || m.thumbnailUrl); if (poster) node.poster = poster; }
      else { node = this.el('img'); node.alt = m.alt || this.l.photo; }
      node.src = src;
      node.addEventListener('error', () => {
        stage.replaceChildren(this.el('p', 'mrc2__notice', this.l.media_error));
        const link = this.el('a', 'mrc2__link', this.l.open_original); link.href = src; link.target = '_blank'; link.rel = 'noopener noreferrer'; stage.append(link);
      }, { once: true }); stage.append(node);
      const caption = this.querySelector('[data-caption]'); caption.replaceChildren(this.el('strong', '', r.reviewer?.displayName || this.l.customer));
      if (r.productName) caption.append(this.el('span', '', r.productName));
      this.querySelector('[data-media-counter]').textContent = (this.mediaIndex + 1) + ' / ' + items.length;
      this.querySelector('[data-media-prev]').disabled = items.length < 2; this.querySelector('[data-media-next]').disabled = items.length < 2;
    }
  });
}
